import { 
  Engine, 
  Scene, 
  TargetCamera, 
  HemisphericLight,
  Vector3,
  StandardMaterial,
  Color3,
  Material,
  MeshBuilder,
  Animation,
  AssetsManager,
  Color4,
} from "@babylonjs/core";
import { Spin } from "antd";
import { useEffect } from "react";
import { useState } from "react";
import { useCallback } from "react";

let isReady = false;

export default function StormyBackground({ className, style, width, height, onReady, onFinish }) {
  const [ canvasRef, setCanvasRef ] = useState(null);
  const [ isLoading, setIsLoading ] = useState(false);

  useEffect(() => {
    setIsLoading(true);
  }, []);

 // Resize canvas
 const updateCanvas = useCallback((canvas) => {
    canvas.width = document.body.clientWidth;
    canvas.height = document.body.clientHeight;
  }, []);

  const handleResize = useCallback(() => {
    if (!canvasRef) {
      return;
    }

    updateCanvas(canvasRef);
  }, [canvasRef, updateCanvas]);

  // Allow for resizing canvas and engine.
  useEffect(() => {
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [handleResize]);

  const canvasRefHandler = useCallback(node => {
    if (!canvasRef && node) {
      setCanvasRef(node);

      updateCanvas(node);
    }
  }, [canvasRef]);

  useEffect(() => {
    if (!canvasRef) {
      return;
    }

    let engine = new Engine(canvasRef, true);
    engine.setHardwareScalingLevel(1.5);

    let scene = new Scene(engine);
    scene.clearColor = new Color4(0, 0, 0, 1);
    
    let assetsManager = new AssetsManager(scene);
    assetsManager.useDefaultLoadingScreen = false;

    // This creates and positions a free camera (non-mesh)
    var camera = new TargetCamera("StarfieldCamera", new Vector3(0, 0, -20), scene);

    // This targets the camera to scene origin
    // camera.setTarget(new Vector3(0, 0, 1));

    // This attaches the camera to the canvas
    camera.attachControl(canvasRef, true);

    // This creates a light, aiming 0,1,0 - to the sky (non-mesh)
    var light = new HemisphericLight("light", new Vector3(0, 1, 0), scene);

    const spaceTask = assetsManager.addTextureTask("space1Task", "assets/space1.png");
    const cloudTask = assetsManager.addTextureTask("space2Task", "assets/space2.png");
    const logoTask = assetsManager.addTextureTask("space3Task", "assets/space3.png");
    
    assetsManager.load();
    assetsManager.onFinish = () => {
      CreateWarpInEffect(scene, camera, spaceTask.texture, cloudTask.texture, logoTask.texture, onFinish);

      isReady = true;
      setTimeout(() => {
        setIsLoading(false);

        setTimeout(() => {
          onReady();
        }, 2000);
      }, 150);
    }

    const renderLoop = () => {
      scene.render();
    };

    engine.runRenderLoop(renderLoop);

    return () => {
      engine.stopRenderLoop();

      setTimeout(() => {
        scene.dispose();

        engine.dispose();
      }, 100);
    }
  }, [canvasRef]);
  
  return (
    <div id="loading-screen">
      <Spin
        spinning={isLoading}
        style={{ height: "100%", maxHeight: "100%" }}
        size="large"
      >
        <canvas
          className={className}
          style={style}
          width={width}
          height={height}
          ref={canvasRefHandler}
        />
      </Spin>
    </div>
  )
}

const startCloudAlpha = 0.25, startStarAlpha = 0.1, startLogoAlpha = 0.15;
/**
 * 
 * @param {Scene} scene 
 * @param {*} camera 
 * @param {*} spaceTexture 
 * @param {*} cloudTexture 
 * @param {*} logoTexture 
 */
function CreateWarpInEffect(scene, camera, spaceTexture, cloudTexture, logoTexture, onFinish) {
  var doStars = true;
  var doClouds = true;
  var doLightning = true;
  var doLogo = true;

  var doCameraSway = true;
  const CameraSway = 0.01;

  const StarCount = 800;
  const StarSpread = 20.0;

  const CloudCount = 70;
  const CloudResetDistance = -10; // -10000
  const CloudSpawnDistance = 5000; // 10000
  const CloudScaleMin = 5;
  const CloudScaleMax = 2.5;

  var LoadingProgress = 0.0; // 0.0 to 1.0

  // Make the distant background
  var SpaceSphere = MeshBuilder.CreateSphere("sphere", { diameter: 10000, segments: 64 }, scene);
  var SpaceMaterial = new StandardMaterial("SpaceMaterial", scene);
  SpaceMaterial.backFaceCulling = false;
  SpaceMaterial.diffuseTexture = spaceTexture;
  SpaceMaterial.diffuseTexture.uScale = 10.0;
  SpaceMaterial.diffuseTexture.vScale = 10.0;
  SpaceMaterial.emissiveTexture = spaceTexture;
  SpaceMaterial.emissiveTexture.uScale = 10.0;
  SpaceMaterial.emissiveTexture.vScale = 10.0;
  SpaceMaterial.transparencyMode = Material.MATERIAL_ALPHABLEND;
  SpaceMaterial.alphaMode = 5;

  SpaceSphere.material = SpaceMaterial;

  if (doStars) {
      // Make the stars
      var Star = MeshBuilder.CreateCylinder(
          "Star", { height: 30, diameterBottom: 0.05, diameterTop: 0.01, tessellation: 16 }, scene);
      Star.position = new Vector3(100, 100, 100);
      Star.rotation = new Vector3(Math.PI / 2, 0., 0.0);

      var StarMaterial = new StandardMaterial("StarMaterial", scene);
      StarMaterial.diffuseColor = Color3.White();
      StarMaterial.emissiveColor = new Color3(0.5, 0.9, 1);
      StarMaterial.alpha = startStarAlpha;
      Star.material = StarMaterial;

      var Stars = [];
      for (var i = 0; i < StarCount; i++) {
          Stars.push(Star.createInstance("star" + i));
          Stars[i].position = new Vector3(
              (Math.random() * 2 - 1) * StarSpread * 2 + 10,
              (Math.random() * 2 - 1) * StarSpread + 10,
              (Math.random() * 2 - 1) * StarSpread * 10
          )
          var s = Math.random();
          Stars[i].scaling = new Vector3(s, 0.8, s);
      }
  }

  if (doClouds) {
      var Cloud = MeshBuilder.CreatePlane("SpaceCloud", { size: 100, }, scene);
      Cloud.position = new Vector3(500, 500, 500);

      var CloudMaterial = new StandardMaterial("cloudMaterial", scene);
      CloudMaterial.diffuseColor = new Color3(0.46, 0.63, 0.67);
      CloudMaterial.diffuseTexture = cloudTexture;
      CloudMaterial.diffuseTexture.hasAlpha = true;
      CloudMaterial.diffuseTexture.useAlphaFromDiffuseTexture = true;
      CloudMaterial.opacityTexture = cloudTexture;
      CloudMaterial.transparencyMode = Material.MATERIAL_ALPHABLEND;
      CloudMaterial.alpha = startCloudAlpha;

      Cloud.material = CloudMaterial;

      var Clouds = [];
      for (var i = 0; i < CloudCount; i++) {
          Clouds.push(Cloud.createInstance("star" + i));
          Clouds[i].position = new Vector3(
              (Math.random() * 2 - 1) * StarSpread * 50,
              (Math.random() * 2 - 1) * StarSpread * 10,
              (Math.random() * CloudSpawnDistance)
          );
          Clouds[i].rotation.z = Math.random() * Math.PI * 10.0;
          var s = Math.random() * CloudScaleMax + CloudScaleMin;
          Clouds[i].scaling = new Vector3(s, s, s);
      }

      if (doLightning) {
          // Make lightning cloud
          var LightningCloud = MeshBuilder.CreatePlane("SpaceCloud", { size: 100, }, scene);
          LightningCloud.position = new Vector3(500, 500, 500);

          var LightningCloudMaterial = new StandardMaterial("cloudMaterial", scene);
          LightningCloudMaterial.diffuseColor = new Color3(0.46, 0.63, 0.67);
          LightningCloudMaterial.diffuseTexture = cloudTexture;
          LightningCloudMaterial.diffuseTexture.hasAlpha = true;
          LightningCloudMaterial.diffuseTexture.useAlphaFromDiffuseTexture = true;
          LightningCloudMaterial.emissiveColor = new Color3(0, 0, 0);
          LightningCloudMaterial.opacityTexture = cloudTexture;
          LightningCloudMaterial.transparencyMode = Material.MATERIAL_ALPHABLEND;

          LightningCloud.material = LightningCloudMaterial;
          
          LightningCloud.otherCloud = null;

          const LightningAnim = new Animation(
              "LightningAnim",
              "material.emissiveColor",
              30,
              Animation.ANIMATIONTYPE_COLOR3,
              Animation.ANIMATIONLOOPMODE_CYCLE);

          const keyFrames = [];
          keyFrames.push({
              frame: 0,
              value: new Color3(0.0, 0.0, 0.0)
          });
          keyFrames.push({
              frame: 3,
              value: new Color3(0.67, 0.79, 0.79)
          });
          keyFrames.push({
              frame: 4,
              value: new Color3(0.0, 0.0, 0.0)
          });
          keyFrames.push({
              frame: 5,
              value: new Color3(0.28, 0.41, 0.41)
          });
          keyFrames.push({
              frame: 29,
              value: new Color3(0.0, 0.0, 0.0)
          });
          keyFrames.push({
              frame: 30,
              value: new Color3(0.0, 0.0, 0.0)
          });
          LightningAnim.setKeys(keyFrames);
          LightningCloud.animations.push(LightningAnim);

          var LightningAnimLoop = function () {
              LightningCloud.otherCloud = Clouds[Math.floor(Clouds.length * Math.random())];
              if (LightningCloud.otherCloud.position.z < 1000) { // Cloud too close, try again
                  LightningAnimLoop();
                  return;
              }
              
              
              scene.beginAnimation(LightningCloud, 0, 60, true);
              setTimeout(() => { LightningAnimLoop(); }, Math.random() * 2000 + 200);
          }

          LightningAnimLoop();
      }
  }

  if (doLogo) {
      var Logo = MeshBuilder.CreatePlane("Logo", { width: 225, height: 44 }, scene);
      Logo.position = new Vector3(0, 0, 500);

      var LogoMaterial = new StandardMaterial("LogoMaterial", scene);
      LogoMaterial.diffuseColor = new Color3(0, 0.88, 1);
      LogoMaterial.diffuseTexture = logoTexture;
      LogoMaterial.diffuseTexture.hasAlpha = true;
      LogoMaterial.diffuseTexture.useAlphaFromDiffuseTexture = true;
      LogoMaterial.emissiveColor = new Color3(0.47, 1, 0.96);
      LogoMaterial.emissiveTexture = logoTexture;
      LogoMaterial.opacityTexture = logoTexture;
      LogoMaterial.transparencyMode = Material.MATERIAL_ALPHABLEND;
      LogoMaterial.alpha = startLogoAlpha;

      Logo.material = LogoMaterial;
  }

  const increment = 1 / (5000 / (1000 / 60));
  scene.registerBeforeRender(function () {
      if (!isReady) {
        return;
      }
    
      LoadingProgress += increment;
      const logInverted = Math.log10((1 - LoadingProgress) * 10);
      let multiplier = isNaN(logInverted) ? 0 : logInverted;

      if (multiplier == 0) {
        onFinish();

        isReady = false;
      }

      if (doStars) {
          StarMaterial.alpha = startStarAlpha * multiplier;

          Stars.forEach((star) => {
              star.position.z -= 1 / 60 * 200;
              if (star.position.z < -50) star.position.z += 100;
          });
      }

      if (doClouds) {
          CloudMaterial.alpha = startCloudAlpha * multiplier;

          Clouds.forEach((cloud) => {
              cloud.position.z -= 1 / 60 * 200;
              if (cloud.position.z < CloudResetDistance) cloud.position.z += CloudSpawnDistance + CloudResetDistance;

          });

          // Follow the cloud we're lighting up
          if (doLightning) {
              if (LightningCloud.otherCloud !== null) {
                  LightningCloud.position = LightningCloud.otherCloud.position;
                  LightningCloud.position.z -= 1; // Just in case we care about depth sorting later on
                  LightningCloud.scaling = LightningCloud.otherCloud.scaling;
                  LightningCloud.rotation = LightningCloud.rotation;
                  
                  LightningCloudMaterial.alpha = multiplier;
              }
          }
      }

      if (doLogo) {
        LogoMaterial.alpha = startLogoAlpha * multiplier;
      }

      scene.clearColor.a = SpaceMaterial.alpha = multiplier;

      if (doCameraSway) {
          camera.rotation.x = Math.sin(Date.now() / 1000) * CameraSway;
          camera.rotation.y = Math.sin(Date.now() / 1250) * CameraSway;
          camera.rotation.z = Math.sin(Date.now() / 1500) * CameraSway;
          SpaceSphere.rotation.z = Math.sin(Date.now() / 1500) * CameraSway * 4;
      }
  });
}